/**
 * Consumer Document Findings Hooks
 *
 * Type-safe hooks for fetching document findings (AI-extracted fields with confidence scores).
 * Used by ConfidenceBadge component to display extraction reliability to borrowers.
 *
 * @module hooks/useDocumentFindings
 * @see Issue #37 - Confidence Display UI
 */

import { RecommendationSeverity } from '@ops-matrix/shared-types';
import { useConsumerDocumentFindings, useConsumerExtractedFieldsForCase, useConsumerFinding } from '@/lib/trpc/hooks';

// Re-export types for external use
// Note: Dates may come as strings from JSON serialization
export interface DocumentFinding {
  id: string;
  documentId: string;
  businessId: string;
  rulesetId: string;
  ruleId: string;
  category: 'health' | 'compliance' | 'redFlag' | 'suggestion' | 'risk';
  severity: `${RecommendationSeverity}`;
  title: string;
  description: string;
  citations: Array<{
    chunkId: string;
    text: string;
    pageNumber?: number;
    confidence: number;
  }>;
  confidence: number;
  status: 'active' | 'resolved' | 'dismissed' | 'false_positive';
  extractedData?: Record<string, unknown>;
  userFeedback?: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface ExtractedField {
  value: string | number | null;
  confidence: number | null;
  documentId: string | null;
}

export interface ExtractedFieldsForCase {
  loanAmount: ExtractedField;
  annualRevenue: ExtractedField;
  creditScore: ExtractedField;
}

export interface DocumentFindingsResponse {
  findings: DocumentFinding[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Fetch document findings for a specific document
 * Returns AI-extracted fields with confidence scores
 *
 * @param documentId - The document UUID to fetch findings for
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useDocumentFindings(documentId);
 * data?.findings.map(finding => (
 *   <ConfidenceBadge confidence={finding.confidence} />
 * ));
 * ```
 */
export function useDocumentFindings(documentId: string | null, page = 1, limit = 20) {
  const query = useConsumerDocumentFindings(documentId, { page, limit });

  // Extract data with explicit typing to avoid excessive type depth
  const transformedData: DocumentFindingsResponse | undefined = query.data
    ? {
        findings: query.data.data as unknown as DocumentFinding[],
        total: query.data.total as number,
        page: query.data.page as number,
        limit: query.data.limit as number,
      }
    : undefined;

  return {
    data: transformedData,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Fetch a single finding by ID
 *
 * @param id - The finding UUID
 */
export function useFinding(id: string | null) {
  return useConsumerFinding(id);
}

/**
 * Fetch aggregated extracted fields for case review
 * Combines findings from multiple documents into a single object with field values and confidence
 *
 * @param documentIds - Array of document UUIDs from the case
 */
export function useExtractedFieldsForCase(documentIds: string[]) {
  return useConsumerExtractedFieldsForCase(documentIds);
}

export default useDocumentFindings;
